import { useMemo } from 'react';
import { useI18n } from '../i18n';
import { googleCalendarAccountHint } from '../i18n/googleCalendarAccountHint';
import {
  buildBookingIcsContent,
  buildGoogleCalendarAddEventUrl,
  downloadBookingIcsFile,
  safeBookingIcsFilename,
  type BookingCalendarEventInput,
} from '../utils/bookingCalendarExport';
import styles from './BookingCalendarExportButtons.module.css';

type BookingCalendarExportButtonsProps = {
  event: BookingCalendarEventInput | null;
  variant?: 'confirmation' | 'owner';
};

export function BookingCalendarExportButtons({
  event,
  variant = 'confirmation',
}: BookingCalendarExportButtonsProps) {
  const { language, t } = useI18n();
  const googleUrl = useMemo(
    () => (event ? buildGoogleCalendarAddEventUrl(event) : null),
    [event],
  );
  const icsContent = useMemo(() => (event ? buildBookingIcsContent(event) : null), [event]);

  if (!googleUrl || !icsContent) {
    return null;
  }

  const onDownloadIcs = () => {
    downloadBookingIcsFile(icsContent, safeBookingIcsFilename(event?.bookingId));
  };

  const rowClass = [styles.row, variant === 'owner' ? styles.rowOwner : ''].filter(Boolean).join(' ');
  const primaryClass = [
    styles.button,
    variant === 'owner' ? styles.buttonOwnerPrimary : styles.buttonPrimary,
  ]
    .filter(Boolean)
    .join(' ');
  const secondaryClass = [
    styles.button,
    variant === 'owner' ? styles.buttonOwner : '',
  ]
    .filter(Boolean)
    .join(' ');

  const hintClass = variant === 'owner' ? styles.hintOwner : styles.hint;

  return (
    <div>
      <div className={rowClass} role="group" aria-label={t('bookings.calendarExport.groupLabel')}>
        <a
          className={primaryClass}
          href={googleUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('bookings.calendarExport.addToGoogle')}
        </a>
        <button type="button" className={secondaryClass} onClick={onDownloadIcs}>
          {t('bookings.calendarExport.downloadIcs')}
        </button>
      </div>
      <p className={hintClass}>{googleCalendarAccountHint(language)}</p>
    </div>
  );
}
